import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { buildingActivitiesService } from '../services'
import type { BuildingActivity } from '@popety-io/types'
import type { ActivityType } from '@popety-io/types'

interface BuildingActivitiesContextType {
  // Data
  activities: BuildingActivity[]
  loading: boolean
  error: string | null
  total: number

  // Filters
  searchTerm: string
  setSearchTerm: (term: string) => void
  activeFilter: ActivityType | 'all'
  setActiveFilter: (filter: ActivityType | 'all') => void

  // Actions
  fetchActivities: (buildingId: string) => Promise<void>
  refreshActivities: () => Promise<void>

  // Current building ID
  currentBuildingId: string | null
}

const BuildingActivitiesContext = createContext<
  BuildingActivitiesContextType | undefined
>(undefined)

export const BuildingActivitiesProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  // Core state
  const [activities, setActivities] = useState<BuildingActivity[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [currentBuildingId, setCurrentBuildingId] = useState<string | null>(
    null
  )

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActivityType | 'all'>('all')

  // Fetch activities for a specific building
  const fetchActivities = useCallback(async (buildingId: string) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentBuildingId(buildingId)

      const response =
        await buildingActivitiesService.getByBuildingId(buildingId)
      setActivities(response.data || [])
      setTotal(response.data?.length || 0)
    } catch (err) {
      console.error('Failed to fetch activities:', err)
      setError('Failed to load activities')
      setActivities([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh activities for current building
  const refreshActivities = useCallback(async () => {
    if (currentBuildingId) {
      await fetchActivities(currentBuildingId)
    }
  }, [currentBuildingId, fetchActivities])

  // Reset filters when building changes
  useEffect(() => {
    setSearchTerm('')
    setActiveFilter('all')
  }, [currentBuildingId])

  const value: BuildingActivitiesContextType = {
    // Data
    activities,
    loading,
    error,
    total,

    // Filters
    searchTerm,
    setSearchTerm,
    activeFilter,
    setActiveFilter,

    // Actions
    fetchActivities,
    refreshActivities,

    // Current building ID
    currentBuildingId,
  }

  return (
    <BuildingActivitiesContext.Provider value={value}>
      {children}
    </BuildingActivitiesContext.Provider>
  )
}

export const useBuildingActivities = () => {
  const context = useContext(BuildingActivitiesContext)
  if (context === undefined) {
    throw new Error(
      'useBuildingActivities must be used within a BuildingActivitiesProvider'
    )
  }
  return context
}
